import {
	NavLink,
	Navigate,
	Outlet,
	useOutletContext,
	useParams,
	useNavigate,
} from "react-router";
import { useEffect, useState } from "react";
import { useAccountStore } from "../../stores/accountStore";
import { useSettingsStore } from "../../stores/settingsStore";
import { useChats, isInvite, href } from "../../util";
import { Shield, Settings, Info, Plus, UserPlus, Grid, Bell } from "lucide-react";
import { cn } from "../../lib/utils";
import { doSubscribePush } from "../auth/Login";
import { InstallPrompt } from "../InstallPrompt";

const AccountAvatar = ({ accountId }: { accountId: string }) => {
	const initials = accountId.split("@")[0].slice(0, 2).toUpperCase();
	return (
		<div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-xs font-medium">
			{initials}
		</div>
	);
};

const NotificationBanner = ({
	accounts,
	idbp,
}: {
	accounts: Record<string, any>;
	idbp: any;
}) => {
	const [permission, setPermission] = useState<string>(
		typeof Notification !== "undefined" ? Notification.permission : "granted",
	);

	if (permission !== "default") return null;

	const request = async () => {
		const result = await Notification.requestPermission();
		setPermission(result);
		if (result === "granted") {
			if (import.meta.env.DEV) console.log("[push] permission granted via banner, subscribing all online accounts");
			for (const account of Object.values(accounts)) {
				const { client, status } = account as any;
				if (client && status === "online") {
					doSubscribePush(client, idbp);
				} else if (import.meta.env.DEV) {
					console.log("[push] skipping account (status:", status, ")");
				}
			}
		}
	};

	return (
		<div className="flex items-center justify-between gap-3 px-4 py-2 bg-primary/15 border-b border-primary/25 shrink-0">
			<div className="flex items-center gap-2 min-w-0">
				<Bell size={14} className="text-primary-light shrink-0" />
				<span className="text-xs text-primary-light truncate">
					Enable notifications to stay in sync
				</span>
			</div>
			<button
				onClick={request}
				className="text-xs font-semibold text-primary-light underline shrink-0"
			>
				Enable
			</button>
		</div>
	);
};

export const Accounts = () => {
	const outletCtx = useOutletContext<any>();
	const { accounts, persistence } = outletCtx;
	const params = useParams();
	const navigate = useNavigate();
	const { messageRequests } = useSettingsStore();

	const account = params.accountId ? accounts[params.accountId] : null;
	const chats = useChats(account?.client);
	const anyApps = chats.some((c) => c.isApp);
	const anyInvites = chats.some((c) => isInvite(messageRequests, c));

	useEffect(() => {
		if (!params.accountId || !persistence) return;
		persistence.set("ui#selectedAccount", params.accountId);
	}, [params.accountId, persistence]);

	if (params.accountId && !accounts[params.accountId]) {
		return <Navigate to="/" />;
	}

	const accountIds = Object.keys(accounts);

	return (
		<div
			className="flex h-screen overflow-hidden"
			style={{ paddingTop: "env(safe-area-inset-top)" }}
		>
			{/* Left nav strip (desktop only) */}
			<nav className="hidden md:flex flex-col items-center gap-2 w-16 py-4 glass-panel border-r border-white/8 shrink-0">
				{accountIds.map((id) => (
					<NavLink
						key={id}
						to={href("/accounts/:accountId", { accountId: id })}
						target="_self"
						title={id}
						className={({ isActive }) =>
							cn(
								"w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-white/10",
								isActive ? "ring-2 ring-primary" : "",
							)
						}
					>
						<AccountAvatar accountId={id} />
					</NavLink>
				))}

				<div className="mt-auto flex flex-col items-center gap-2">
					{anyInvites && (
						<NavLink
							to={
								params.accountId
									? href("/accounts/:accountId/requests", {
											accountId: params.accountId,
										})
									: "/"
							}
							target="_self"
							className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
							title="Message Requests"
						>
							<UserPlus size={20} />
						</NavLink>
					)}
					{anyApps && (
						<NavLink
							to={
								params.accountId
									? href("/accounts/:accountId/apps", {
											accountId: params.accountId,
										})
									: "/"
							}
							target="_self"
							className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
							title="Apps"
						>
							<Grid size={20} />
						</NavLink>
					)}
					<NavLink
						to="/admin"
						target="_self"
						className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
						title="Admin"
					>
						<Shield size={20} />
					</NavLink>
					<NavLink
						to="/settings"
						target="_self"
						className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
						title="Settings"
					>
						<Settings size={20} />
					</NavLink>
					<NavLink
						to="/about"
						target="_self"
						className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
						title="About"
					>
						<Info size={20} />
					</NavLink>
					<NavLink
						to="/login"
						target="_self"
						className="w-10 h-10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
						title="Add Account"
					>
						<Plus size={20} />
					</NavLink>
				</div>
			</nav>

			{/* Main content area */}
			<div className="flex-1 flex flex-col overflow-hidden">
				<NotificationBanner accounts={accounts} idbp={outletCtx.idbp} />
				<InstallPrompt />
				<div className="flex-1 overflow-hidden">
					<Outlet
						context={{ ...outletCtx, client: account?.client, status: account?.status }}
					/>
				</div>
			</div>
		</div>
	);
};
